import React, { useState, useCallback, ErrorInfo } from "react";

export interface ErrorBoundaryProps {
  Fallback?: React.ComponentType<{ resetError: () => void; retry: () => void }>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
  onRetry?: () => Promise<void>;
  retryIntervals?: number[] | number;
  maxRetry?: number;
  children?: React.ReactNode;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  retryCount = 0;
  retryTimeoutId: NodeJS.Timeout | undefined;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError?.(error, errorInfo);
    console.error("Error caught in ErrorBoundary:", error);
    console.error("React error info:", errorInfo);
  }

  componentDidUpdate(_prevProps: ErrorBoundaryProps, prevState: ErrorBoundaryState) {
    if (!prevState.hasError && this.state.hasError && this.props.onRetry) {
      this.retry();
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  getRetryInterval = () => {
    if (typeof this.props.retryIntervals === "number") {
      return this.props.retryIntervals;
    }
    return this.props.retryIntervals && this.retryCount < this.props.retryIntervals.length
      ? this.props.retryIntervals[this.retryCount]
      : this.props.retryIntervals?.[this.props.retryIntervals.length - 1];
  };

  retry = async () => {
    const retryInterval = this.getRetryInterval();
    const maxRetry = this.props.maxRetry ?? 10;

    if (!this.props.onRetry || retryInterval === undefined || this.retryCount >= maxRetry) {
      return;
    }

    this.retryTimeoutId = setTimeout(async () => {
      try {
        if (this.props.onRetry) {
          await this.props.onRetry();
        }
        this.retryCount = 0;
        this.resetError();
      } catch {
        this.retryCount += 1;
        this.retry();
      }
    }, retryInterval);
  };

  resetError = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (!this.props.Fallback) {
        return <h1>Oops! Something went wrong. Please try again.</h1>;
      }
      return (
        <this.props.Fallback
          resetError={this.resetError}
          retry={this.retry}
        />
      );
    }

    return this.props.children;
  }
}

function useErrorBoundary() {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const triggerError = useCallback((error: Error) => {
    setHasError(true);
    setError(error);
  }, []);

  const resetError = useCallback(() => {
    setHasError(false);
    setError(null);
  }, []);

  return { hasError, error, triggerError, resetError };
}

function withErrorBoundary(WrappedComponent: React.ComponentType<any>, boundaryProps: ErrorBoundaryProps) {
  return (componentProps: any) => (
    <ErrorBoundary {...boundaryProps}>
      <WrappedComponent {...componentProps} />
    </ErrorBoundary>
  );
}

export { ErrorBoundary, useErrorBoundary, withErrorBoundary };
